import React, { useState } from 'react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { toast } from 'sonner';

interface BrandProfileEditDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: 'overview' | 'company' | 'marketing' | 'athletes' | 'contact';
  data: any;
  onSave: (updatedData: any) => void;
}

const BrandProfileEditDrawer: React.FC<BrandProfileEditDrawerProps> = ({
  open,
  onOpenChange,
  section,
  data,
  onSave
}) => {
  const [formData, setFormData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await onSave(formData);
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const renderOverviewFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="display_name">Display Name</Label>
        <Input
          id="display_name"
          value={formData.display_name || ''}
          onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bio">Bio</Label>
        <Textarea
          id="bio"
          value={formData.bio || ''}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="location">Location</Label>
        <Input
          id="location"
          value={formData.location || ''}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
        />
      </div>
    </>
  );

  const renderCompanyFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="company_name">Company Name</Label>
        <Input
          id="company_name"
          value={formData.brand?.company_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, company_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="industry">Industry</Label>
        <Input
          id="industry"
          value={formData.brand?.industry || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, industry: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="company_size">Company Size</Label>
        <Select
          value={formData.brand?.company_size || ''}
          onValueChange={(value) => setFormData({
            ...formData,
            brand: { ...formData.brand, company_size: value }
          })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select company size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1-10">1-10 employees</SelectItem>
            <SelectItem value="11-50">11-50 employees</SelectItem>
            <SelectItem value="51-200">51-200 employees</SelectItem>
            <SelectItem value="201-500">201-500 employees</SelectItem>
            <SelectItem value="501-1000">501-1000 employees</SelectItem>
            <SelectItem value="1000+">1000+ employees</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          value={formData.brand?.website || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, website: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.brand?.description || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, description: e.target.value }
          })}
          rows={3}
        />
      </div>
    </>
  );

  const renderMarketingFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="budget_range">Budget Range</Label>
        <Select
          value={formData.brand?.budget_range || ''}
          onValueChange={(value) => setFormData({
            ...formData,
            brand: { ...formData.brand, budget_range: value }
          })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select budget range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="$1K-$5K">$1K-$5K</SelectItem>
            <SelectItem value="$5K-$10K">$5K-$10K</SelectItem>
            <SelectItem value="$10K-$25K">$10K-$25K</SelectItem>
            <SelectItem value="$25K-$50K">$25K-$50K</SelectItem>
            <SelectItem value="$50K-$100K">$50K-$100K</SelectItem>
            <SelectItem value="$100K+">$100K+</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="marketing_objectives">Marketing Objectives</Label>
        <Textarea
          id="marketing_objectives"
          value={formData.brand?.marketing_objectives || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, marketing_objectives: e.target.value }
          })}
          rows={3}
        />
      </div>
    </>
  );

  const renderContactFields = () => (
    <>
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_person_name">Contact Person</Label>
        <Input
          id="contact_person_name"
          value={formData.brand?.contact_person_name || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, contact_person_name: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_person_title">Contact Person Title</Label>
        <Input
          id="contact_person_title"
          value={formData.brand?.contact_person_title || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, contact_person_title: e.target.value }
          })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="contact_phone">Phone</Label>
        <Input
          id="contact_phone"
          value={formData.brand?.contact_phone || ''}
          onChange={(e) => setFormData({
            ...formData,
            brand: { ...formData.brand, contact_phone: e.target.value }
          })}
        />
      </div>
    </>
  );

  const getSectionTitle = () => {
    switch (section) {
      case 'overview': return 'Edit Overview';
      case 'company': return 'Edit Company Details';
      case 'marketing': return 'Edit Marketing Focus';
      case 'contact': return 'Edit Contact Info';
      default: return 'Edit Profile';
    }
  };

  const renderFields = () => {
    switch (section) {
      case 'overview': return renderOverviewFields();
      case 'company': return renderCompanyFields();
      case 'marketing': return renderMarketingFields();
      case 'contact': return renderContactFields();
      default: return null;
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <div className="flex items-center justify-between">
            <DrawerTitle>{getSectionTitle()}</DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="px-4 pb-4 space-y-4">
          {renderFields()}
          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} disabled={isLoading} className="flex-1">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default BrandProfileEditDrawer;